import React, { useEffect, useLayoutEffect, useState } from "react";
import {
    Archive as ArchiveIcon,
    AutoAwesome as AutoAwesomeIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    FormatQuote as FormatQuoteIcon,
    GridOn as GridIcon,
    HelpOutline as HelpOutlineIcon,
    LightbulbOutlined as LightbulbOutlinedIcon,
    Home as HomeIcon,
    Dashboard as DashboardIcon,
    MenuBook as MenuBookIcon,
    Today as TodayIcon,
    Login as LoginIcon,
    MessageOutlined as HiddenMessageIcon,
    ExitToApp as LogoutIcon,
    ManageAccounts as ManageAccountsIcon,
    Person as PersonIcon,
    PhoneAndroid as PhoneAndroidIcon,
    School as SchoolIcon,
    Search as SearchIcon,
    Spellcheck as SpellcheckIcon,
    ViewModule as SlidingNavIcon,
    Extension as ExtensionIcon,
    DonutLarge as WordWheelNavIcon,
} from "@mui/icons-material";
import SettingsIcon from '@mui/icons-material/Settings';
import { Avatar, Box, Chip, Divider, Drawer, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService, { AUTH_CHANGED_EVENT } from "../../services/AuthService";
import Logger from "../../services/Logger";
import UserService from "../../services/UserService";
import WebPushService from "../../services/WebPushService";
import UtilService from "../../services/UtilService";
import AudioButton from "../components/AudioButton";
import { HOME_BGM_TRACKS } from "../components/bgmTracks";
import {
    BGM_AUTO_PLAY_EVENT,
    readBgmAutoPlayPreference,
    resolveBgmAutoPlayPreference,
    writeBgmAutoPlayPreference,
} from "../components/bgmPreference";
import MinimalHeader from "../components/puzzle/MinimalHeader";
import AdSenseBanner, { ADSENSE_ENABLED } from "../components/AdSenseBanner";
import { clearMonetagPageAds, MonetagInpagePushTag } from "../components/MonetagAds";
import { accountHasNoAdsPurchase } from "../profile/noAdsOffer";
import SiteMarketingFooter from "../footer/SiteMarketingFooter";
import SubscriptionDialog from "../subscription/SubscriptionDialog";
import ReferralEventMiniSection from "../referral/ReferralEventMiniSection";
import { UserLayoutContext } from "./UserLayoutContext";
import { SITE_NAV_THEME } from "../tutors/tutorsNavTheme";
import TutorsSubNav from "../tutors/TutorsSubNav";
import { usePagesTranslation } from "../locales";
import { getAppCopyrightLine } from "../../config/appCopyright";
import { shouldUseLegalAppShell } from "../../config/appWebViewLegal";
import { ACCENT, SITE_BG, TEXT } from "../../dark-theme/palette";
import UserThemeProvider from "../../dark-theme/UserThemeProvider";
import { getLandingWeeklyTheme } from "../login/landingWeeklyTheme";

/** Immersive puzzle board play — no site menu, logo, story title, or sign-out bar. */
function isImmersivePuzzleBoardPath(pathname) {
    return (
        /^\/puzzle\/play-jigsaw\/[^/]+\/board\/[^/]+\/basket\/[^/]+\/?$/.test(pathname) ||
        /^\/puzzle\/play-jigsaw\/[^/]+\/board\/[^/]+\/?$/.test(pathname) ||
        /^\/puzzle\/play-sliding\/[^/]+\/board\/[^/]+\/?$/.test(pathname)
    );
}

/** Ambient mist pages — match backdrop so no white gap under content. */
function isMistSelectLevelPath(pathname) {
    const path = pathname || "";
    if (path === "/word-of-day" || path.startsWith("/word-of-day/")) return true;
    if (/^\/puzzle\/play-cross\/[^/]+\/?$/.test(path)) return true;
    if (/^\/story\/play-cross\/[^/]+\/?$/.test(path)) return true;
    if (/^\/puzzle\/play-hidden-message\/[^/]+\/?$/.test(path)) return true;
    if (/^\/puzzle\/play-word-search\/[^/]+\/?$/.test(path)) return true;
    if (/^\/story\/play-hidden-message\/[^/]+\/?$/.test(path)) return true;
    if (/^\/story\/play-word-search\/[^/]+\/?$/.test(path)) return true;
    if (/\/board\//.test(path)) return false;
    return (
        /^\/puzzle\/play-sliding\/[^/]+\/?$/.test(path) ||
        /^\/puzzle\/play-jigsaw\/[^/]+\/?$/.test(path)
    );
}

/** Support / marketing / privacy / terms — no AdSense on these pages. */
function isNoAdSensePublicPath(pathname) {
    const path = (pathname || "").replace(/\/+$/, "") || "/";
    if (path === "/support" || path.startsWith("/support/")) return true;
    if (path === "/marketing" || path.startsWith("/marketing/")) return true;
    if (path === "/legal" || path.startsWith("/legal/")) return true;
    if (path === "/privacy" || path === "/privacy_policy" || path.startsWith("/privacy_policy/")) return true;
    if (path === "/terms" || path === "/terms_of_service" || path.startsWith("/terms_of_service/")) return true;
    return false;
}

const UserLayout = ({ children }) => {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [userSubscription, setUserSubscription] = useState('free');
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [subscriptionTriggerReason, setSubscriptionTriggerReason] = useState('general');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [expandedItems, setExpandedItems] = useState({});
    const [puzzleTitle, setPuzzleTitle] = useState(null);
    const [puzzleSubtitle, setPuzzleSubtitle] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [bgmAutoPlay, setBgmAutoPlay] = useState(() => resolveBgmAutoPlayPreference(true));
    const [audioPrefsReady, setAudioPrefsReady] = useState(false);
    const loadUserData = async () => {
        try {
            const authStatus = await AuthService.isAuthenticated();
            setIsAuthenticated(authStatus);

            // Logger.log("authStatus", authStatus);
            if (authStatus) {
                // Logger.log("sessionInfo", sessionInfo);

                const u = await UserService.getUserInfo();
                setUser(u);
                const settings = UserService.getSettingsFormData(u);
                const stored = readBgmAutoPlayPreference();
                if (settings.bgmAutoPlay === false) {
                    writeBgmAutoPlayPreference(false);
                    setBgmAutoPlay(false);
                } else {
                    setBgmAutoPlay(stored === null ? true : stored);
                }
                WebPushService.syncFromPreference(settings.notifications !== false, {
                    requestPermission: false,
                }).catch((error) => Logger.error("[WebPush] sync on login failed", error));

                // Determine subscription status based on payPlan
                if (u?.cloudUser) {
                    if (UtilService.isStandardPayPlanForUser(u.cloudUser) || u.cloudUser.userRole === 'ADMIN') {
                        setUserSubscription('premium');
                    } else {
                        setUserSubscription('free');
                    }
                } else {
                    setUserSubscription('free');
                }
            } else {
                setUser(null);
                setUserSubscription('free');
                const stored = readBgmAutoPlayPreference();
                setBgmAutoPlay(stored === null ? true : stored);
            }
            setAudioPrefsReady(true);
        } catch (error) {
            Logger.error('Error checking auth or loading user:', error);
            setIsAuthenticated(false);
            setUser(null);
            const stored = readBgmAutoPlayPreference();
            setBgmAutoPlay(stored === null ? true : stored);
            setAudioPrefsReady(true);
        }
    };

    useEffect(() => {
        loadUserData();
        const onAuthChanged = () => loadUserData();
        window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
        return () => window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    }, []);

    useEffect(() => {
        const onBgmPref = (event) => {
            const enabled = event?.detail?.enabled;
            if (typeof enabled === "boolean") setBgmAutoPlay(enabled);
        };
        window.addEventListener(BGM_AUTO_PLAY_EVENT, onBgmPref);
        return () => window.removeEventListener(BGM_AUTO_PLAY_EVENT, onBgmPref);
    }, []);

    // After SPA navigation, reset window scroll so MinimalHeader (menu, title, sign out) is not above the viewport.
    useLayoutEffect(() => {
        if (typeof window === "undefined") return;
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, [location.pathname]);

    // Auto-expand Account menu when on account-related pages or when logged in (for discoverability)
    useEffect(() => {
        const accountPaths = ['/account/profile', '/account/settings', '/account/vocabulary', '/account/library'];
        if (accountPaths.some(path => location.pathname === path) || isAuthenticated) {
            setExpandedItems(prev => ({
                ...prev,
                account: true
            }));
        }
    }, [location.pathname, isAuthenticated]);

    // Auto-expand How to Play menu when on howto pages
    useEffect(() => {
        if (location.pathname.startsWith('/howto')) {
            setExpandedItems(prev => ({
                ...prev,
                "how-to-play": true
            }));
        }
    }, [location.pathname]);

    // Auto-expand Word puzzles when browsing crossword / hidden message / word search
    useEffect(() => {
        const wordPuzzlePaths = ['/word-of-day', '/storybook', '/puzzles/crossword', '/puzzles/hidden-message', '/puzzles/word-search'];
        if (wordPuzzlePaths.includes(location.pathname)) {
            setExpandedItems(prev => ({
                ...prev,
                'word-puzzles': true
            }));
        }
    }, [location.pathname]);

    // Listen for avatar updates
    useEffect(() => {
        const handleAvatarUpdate = () => {
            Logger.log('Avatar updated, refreshing user data...');
            loadUserData();
        };

        window.addEventListener('avatarUpdated', handleAvatarUpdate);

        return () => {
            window.removeEventListener('avatarUpdated', handleAvatarUpdate);
        };
    }, []);

    // Clear puzzle header lines when navigating away from embedded play surfaces
    useEffect(() => {
        const isEmbeddedPuzzleHeader =
            location.pathname.startsWith("/puzzle/play-cross/") ||
            location.pathname.startsWith("/puzzle/play-hidden-message/") ||
            location.pathname.startsWith("/puzzle/play-word-search/") ||
            location.pathname.startsWith("/puzzle/play-grid/") ||
            location.pathname.startsWith("/story/play-cross/") ||
            location.pathname.startsWith("/story/play-grid/");

        if (!isEmbeddedPuzzleHeader) {
            setPuzzleTitle(null);
            setPuzzleSubtitle(null);
        }
    }, [location.pathname]);

    const handleUpgradeRequired = (reason) => {
        setSubscriptionTriggerReason(reason);
        setSubscriptionModalOpen(true);
    };

    const handleProfileClick = () => {
        navigate('/account/profile');
        setDrawerOpen(false);
    };

    const handleSubscribe = (plan) => {
        // Handle subscription logic here
        Logger.log('Subscribing to plan:', plan);
        // Mock subscription update
        setUserSubscription('premium');
    };

    const handleLogout = async () => {
        try {
            await AuthService.logout();
        } catch (error) {
            Logger.error('Logout error:', error);
        }
    };

    const handleLogin = async () => {
        try {
            await AuthService.login();
        } catch (error) {
            Logger.error('Login error:', error);
        }
    };

    const handleMenuClick = () => {
        setDrawerOpen(true);
    };

    const handleNotificationClick = () => {
        // Handle notification click
        Logger.log('Notification clicked');
    };

    const handleNavigation = (path) => {
        navigate(path);
        setDrawerOpen(false);
    };

    const handleLogoutFromMenu = () => {
        setDrawerOpen(false);
        if (isAuthenticated) {
            handleLogout();
        } else {
            handleLogin();
        }
    };

    const handleSubMenuToggle = (itemKey) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemKey]: !prev[itemKey]
        }));
    };

    const onTutorsHub =
        location.pathname === "/tutors" || location.pathname.startsWith("/tutors/");
    const nav = SITE_NAV_THEME;

    const accountMenuItem = {
        key: 'account',
        textKey: 'nav.account',
        icon: <PersonIcon />,
        path: '/account',
        subItems: [
            { key: 'profile', textKey: 'nav.profile', icon: <PersonIcon />, path: '/account/profile' },
            { key: 'settings', textKey: 'nav.settings', icon: <SettingsIcon />, path: '/account/settings' },
            { key: 'library', textKey: 'nav.library', icon: <ArchiveIcon />, path: '/account/library' },
        ]
    };

    const menuItems = [
        { key: 'home', textKey: 'nav.home', icon: <HomeIcon />, path: '/' },
        ...(isAuthenticated
            ? [{ key: 'dashboard', textKey: 'nav.dashboard', icon: <DashboardIcon />, path: '/dashboard' }]
            : []),
        {
            key: 'browse-jigsaw',
            textKey: 'nav.jigsaw',
            icon: <ExtensionIcon />,
            path: '/puzzles/jigsaw',
        },
        {
            key: 'browse-word-wheel',
            textKey: 'nav.word-wheel',
            icon: <WordWheelNavIcon />,
            path: '/puzzles/word-wheel',
        },
        {
            key: 'browse-sliding',
            textKey: 'nav.sliding',
            icon: <SlidingNavIcon />,
            path: '/puzzles/sliding',
        },
        {
            key: 'word-puzzles',
            textKey: 'nav.word-puzzles',
            icon: <SpellcheckIcon />,
            subItems: [
                { key: 'word-of-day', textKey: 'nav.word-of-day', icon: <TodayIcon />, path: '/word-of-day' },
                { key: 'storybook', textKey: 'nav.storybook', icon: <MenuBookIcon />, path: '/storybook' },
                { key: 'browse-crossword', textKey: 'nav.crossword', icon: <GridIcon />, path: '/puzzles/crossword' },
                { key: 'browse-hidden-message', textKey: 'nav.hidden-msg', icon: <HiddenMessageIcon />, path: '/puzzles/hidden-message' },
                { key: 'browse-word-search', textKey: 'nav.word-search', icon: <SearchIcon />, path: '/puzzles/word-search' },
            ],
        },
        {
            key: 'tutors',
            textKey: 'nav.tutors-menu',
            icon: <SchoolIcon />,
            path: '/tutors',
        },
        {
            key: 'how-to-play',
            textKey: 'nav.how-to-play',
            icon: <HelpOutlineIcon />,
            path: '/howto/puzzle/jigsaw',
            navigateOnExpand: true,
            subItems: [
                { key: 'howto-puzzle-jigsaw', textKey: 'nav.howto-puzzle-jigsaw', icon: <ExtensionIcon />, path: '/howto/puzzle/jigsaw' },
                { key: 'howto-puzzle-sliding', textKey: 'nav.howto-puzzle-sliding', icon: <SlidingNavIcon />, path: '/howto/puzzle/sliding' },
                { key: 'howto-puzzle-word-wheel', textKey: 'nav.howto-puzzle-word-wheel', icon: <WordWheelNavIcon />, path: '/howto/puzzle/word-wheel' },
                { key: 'howto-puzzle-crossword', textKey: 'nav.howto-puzzle-crossword', icon: <GridIcon />, path: '/howto/puzzle/crossword' },
                { key: 'howto-puzzle-hidden', textKey: 'nav.howto-puzzle-hidden', icon: <HiddenMessageIcon />, path: '/howto/puzzle/hidden-message' },
                { key: 'howto-puzzle-word-search', textKey: 'nav.howto-puzzle-word-search', icon: <SearchIcon />, path: '/howto/puzzle/word-search' },
                { key: 'howto-assistant', textKey: 'nav.howto-assistant', icon: <AutoAwesomeIcon color="secondary" />, path: '/howto/assistant' },
                { key: 'howto-tips', textKey: 'nav.howto-tips', icon: <LightbulbOutlinedIcon />, path: '/howto/tips' },
                { key: 'howto-details', textKey: 'nav.howto-details', icon: <FormatQuoteIcon />, path: '/howto/hyphens' }
            ]
        },
        {
            key: 'mobile-apps',
            textKey: 'nav.mobile-apps',
            icon: <PhoneAndroidIcon />,
            path: '/mobile-apps',
        },
        ...(isAuthenticated ? [accountMenuItem] : [])
    ];

    const navDisplayName = UserService.getNavDisplayName(user);
    const legalAppShell = shouldUseLegalAppShell(
        location.pathname,
        location.search,
        location.hash,
    );
    const immersiveBoardPlay = isImmersivePuzzleBoardPath(location.pathname);
    const mistSelectLevel = isMistSelectLevelPath(location.pathname);
    /** Editorial landing kept at /landing — still uses the weekly mist backdrop. */
    const isEditorialLanding = location.pathname === "/landing";
    const isHomePage = location.pathname === "/" || isEditorialLanding;
    const editorialLandingBase = isEditorialLanding ? getLandingWeeklyTheme().base : null;
    const hideSiteHeader = immersiveBoardPlay || legalAppShell;
    const adsSuppressed = accountHasNoAdsPurchase(user?.cloudUser)
        || UtilService.isStandardPayPlanForUser(user?.cloudUser);
    const showAdBanner = !adsSuppressed
        && ADSENSE_ENABLED
        && !immersiveBoardPlay
        && !legalAppShell
        && !isNoAdSensePublicPath(location.pathname);
    const showMonetagBanner = !adsSuppressed
        && !legalAppShell
        && !isNoAdSensePublicPath(location.pathname);
    useEffect(() => {
        if (adsSuppressed) clearMonetagPageAds();
    }, [adsSuppressed]);
    useEffect(() => {
        if (!immersiveBoardPlay) return undefined;
        document.documentElement.classList.add("pi-immersive-play");
        return () => document.documentElement.classList.remove("pi-immersive-play");
    }, [immersiveBoardPlay]);

    return (
        <UserThemeProvider>
        <Box
            sx={{
                width: "100%",
                m: 0,
                ...(immersiveBoardPlay
                    ? {
                          height: "100%",
                          minHeight: "100%",
                          maxHeight: "100%",
                          overflow: "hidden",
                          backgroundColor: SITE_BG,
                      }
                    : {
                          minHeight: "100dvh",
                          /** Editorial landing at /landing keeps its weekly mist wash; every other user page takes the site backdrop. */
                          backgroundColor: isEditorialLanding
                              ? editorialLandingBase || "#c8d9e8"
                              : SITE_BG,
                          ...(isEditorialLanding ? {} : { color: TEXT.primary }),
                          ...(mistSelectLevel
                              ? {
                                    display: "flex",
                                    flexDirection: "column",
                                }
                              : {}),
                      }),
            }}
        >
            {/* Scrolls with the page (not pinned) — same scroll as main content */}
            {!hideSiteHeader ? (
                <MinimalHeader
                    title={process.env.REACT_APP_SITE_NAME || "Puzzle Interact"}
                    onMenuClick={handleMenuClick}
                    showNotifications={true}
                    onNotificationClick={handleNotificationClick}
                    centeredTitle={puzzleTitle}
                    centeredSubtitle={puzzleSubtitle}
                    tone={isEditorialLanding ? "light" : "dark"}
                    headerExtra={
                        isHomePage && audioPrefsReady ? (
                            <AudioButton
                                key={`home-bgm-${bgmAutoPlay}`}
                                tracks={HOME_BGM_TRACKS}
                                defaultPlaying={bgmAutoPlay}
                                cloudUserId={user?.cloudUser?.id}
                            />
                        ) : null
                    }
                />
            ) : null}

            {!hideSiteHeader && isAuthenticated && onTutorsHub ? <TutorsSubNav /> : null}

            <Box
                sx={{
                    position: "relative",
                    ...(immersiveBoardPlay
                        ? { width: "100%", height: "100%", minHeight: "100%", overflow: "hidden" }
                        : mistSelectLevel
                          ? {
                                flex: showAdBanner ? "0 0 auto" : 1,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 0,
                            }
                          : {}),
                }}
            >
                <UserLayoutContext.Provider value={{
                    onUpgradeRequired: handleUpgradeRequired,
                    onPuzzleTitleChange: setPuzzleTitle,
                    onPuzzleSubtitleChange: setPuzzleSubtitle,
                    hasSiteAdBanner: showAdBanner,
                }}>
                    {children}
                </UserLayoutContext.Provider>
            </Box>

            {showMonetagBanner ? <MonetagInpagePushTag /> : null}
            {showAdBanner ? <AdSenseBanner variant="page" /> : null}

            {location.pathname === "/storybook" ? (
                <Box
                    component="footer"
                    sx={{
                        width: "100%",
                        mt: 1,
                        pt: 2,
                        pb: 2,
                        px: { xs: 2, sm: 3 },
                        borderTop: "1px solid",
                        borderColor: "divider",
                        boxSizing: "border-box",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", lineHeight: 1.5 }}
                    >
                        {getAppCopyrightLine() || t("landing.copyright")}
                    </Typography>
                </Box>
            ) : null}

            {!immersiveBoardPlay && !legalAppShell && location.pathname !== "/storybook" ? (
                <SiteMarketingFooter tone={isEditorialLanding ? "light" : "dark"} />
            ) : null}

            {/* Navigation Drawer — hidden in iOS legal WebView (?platform=app) */}
            {!legalAppShell ? (
            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 280,
                        boxSizing: 'border-box',
                        backgroundColor: nav.paper,
                        backgroundImage: 'none',
                        color: nav.text,
                        colorScheme: 'dark',
                    }
                }}
            >
                {/* User Info */}
                <Box sx={{
                    p: 3,
                    borderBottom: `1px solid ${nav.border}`,
                    backgroundColor: nav.header,
                    borderRadius: '8px 8px 0 0'
                }}>
                    <Box sx={{ textAlign: 'center' }}>
                        {/* Avatar */}
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            {!user?.cloudUser ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Avatar
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            backgroundColor: '#6c757d',
                                            border: '2px solid #6c757d'
                                        }}
                                    >
                                        <PersonIcon sx={{ fontSize: 30 }} />
                                    </Avatar>
                                    <Typography variant="subtitle1" sx={{ color: nav.textMuted, mt: 1 }}>
                                        {t("nav.guest")}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>

                        {user?.cloudUser && (
                            <>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        color: nav.text,
                                        mb: 0.5,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'normal',
                                        maxWidth: '100%',
                                        wordBreak: 'break-all',
                                        lineHeight: 1.3,
                                        minHeight: '1.3em',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical'
                                    }}
                                    title={navDisplayName}
                                >
                                    {navDisplayName}
                                </Typography>

                                {/* Subscription Status */}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: userSubscription === 'premium' ? ACCENT.main : nav.textMuted,
                                        fontWeight: 500,
                                        display: 'inline-block',
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 1,
                                        backgroundColor: userSubscription === 'premium' ? ACCENT.soft : nav.hover
                                    }}
                                >
                                    {userSubscription === 'premium' ? t("nav.standard-member") : t("nav.free-user")}
                                </Typography>

                                {/* Small avatar under subscription */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                    {user.cloudUser.details?.avatarUrl ? (
                                        <Avatar
                                            src={user.cloudUser.details.avatarUrl}
                                            alt="User Avatar"
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                cursor: 'pointer',
                                                border: '2px solid #3BA666',
                                                '&:hover': {
                                                    opacity: 0.8,
                                                    transform: 'scale(1.05)',
                                                    transition: 'all 0.2s ease-in-out'
                                                }
                                            }}
                                            onClick={handleProfileClick}
                                        />
                                    ) : (
                                        <Tooltip title={t("nav.edit-profile")} arrow>
                                            <Avatar
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    cursor: 'pointer',
                                                    backgroundColor: '#3BA666',
                                                    border: '2px solid #3BA666',
                                                    '&:hover': {
                                                        backgroundColor: '#2d8a4f',
                                                        transform: 'scale(1.05)',
                                                        transition: 'all 0.2s ease-in-out'
                                                    }
                                                }}
                                                onClick={handleProfileClick}
                                            >
                                                <ManageAccountsIcon sx={{ fontSize: 20 }} />
                                            </Avatar>
                                        </Tooltip>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Navigation Menu */}
                <List sx={{ pt: 1, px: 1, overflow: "visible" }}>
                    {menuItems.map((item, index) => {
                        const pathMatchesNav = (declared) => {
                            if (!declared) return false;
                            if (declared === "/tutors") {
                                return location.pathname === "/tutors" || location.pathname.startsWith("/tutors/");
                            }
                            return location.pathname === declared;
                        };
                        const isActive =
                            (item.path && pathMatchesNav(item.path)) ||
                            Boolean(item.subItems && item.subItems.some((si) => pathMatchesNav(si.path)));

                        return (
                        <React.Fragment key={item.key || index}>
                            <ListItem
                                component="button"
                                onClick={item.subItems
    ? () => {
        const isExpanding = !expandedItems[item.key];
        handleSubMenuToggle(item.key);
        if (isExpanding && item.path && item.navigateOnExpand) handleNavigation(item.path);
    }
    : (item.onClick || (() => handleNavigation(item.path)))}
                                sx={{
                                    overflow: 'visible',
                                    backgroundColor: isActive ? nav.activeBg : 'transparent',
                                    color: isActive ? nav.activeText : nav.textMuted,
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    mb: 0.5,
                                    border: isActive ? `2px solid ${nav.activeBorder}` : '1px solid transparent',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: isActive ? nav.activeBg : nav.hover,
                                        border: isActive ? `2px solid ${nav.activeHoverBorder}` : `1px solid ${nav.border}`,
                                        transform: 'translateX(2px)',
                                        boxShadow: nav.hoverShadow
                                    },
                                    '&:active': {
                                        transform: 'translateX(1px)',
                                        boxShadow: nav.activeShadow
                                    }
                                }}
                            >
                                <ListItemIcon sx={{
                                    color: isActive ? nav.activeText : nav.textMuted,
                                    minWidth: 40,
                                    transition: 'color 0.2s ease-in-out'
                                }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{ component: "div" }}
                                    primary={
                                        <Box
                                            sx={{
                                                position: "relative",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                width: "fit-content",
                                                maxWidth: "100%",
                                                overflow: "visible",
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                sx={{
                                                    color: "inherit",
                                                    fontWeight: "inherit",
                                                    minWidth: 0,
                                                }}
                                            >
                                                {t(item.textKey)}
                                            </Typography>
                                            {item.navBadgeKey ? (
                                                <Chip
                                                    label={t(item.navBadgeKey)}
                                                    size="small"
                                                    color="secondary"
                                                    sx={{
                                                        position: "absolute",
                                                        top: -11,
                                                        left: "50%",
                                                        zIndex: 1,
                                                        height: 16,
                                                        minHeight: 16,
                                                        pointerEvents: "none",
                                                        fontSize: "0.58rem",
                                                        fontWeight: 800,
                                                        letterSpacing: "0.04em",
                                                        transform: "translateX(-50%) rotate(-12deg)",
                                                        boxShadow: "0 1px 4px rgba(0,0,0,0.28)",
                                                        "& .MuiChip-label": { px: 0.6, py: 0 },
                                                    }}
                                                />
                                            ) : null}
                                        </Box>
                                    }
                                    sx={{
                                        overflow: "visible",
                                        color: isActive ? nav.activeText : nav.textMuted,
                                        fontWeight: isActive ? '600' : '500',
                                        fontSize: '0.95rem',
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                />
                                {item.subItems && (
                                    <Box sx={{
                                        color: isActive ? nav.activeText : nav.textMuted,
                                        transition: 'color 0.2s ease-in-out'
                                    }}>
                                        {expandedItems[item.key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </Box>
                                )}
                            </ListItem>

                            {/* Sub-menu items */}
                            {item.subItems && expandedItems[item.key] && (
                                <List sx={{ pl: 3, pr: 1 }}>
                                    {item.subItems.map((subItem, subIndex) => {
                                        const isSubItemActive = location.pathname === subItem.path;
                                        return (
                                        <ListItem
                                            key={subIndex}
                                            component="button"
                                            onClick={subItem.onClick || (() => handleNavigation(subItem.path))}
                                            sx={{
                                                backgroundColor: isSubItemActive ? nav.activeBg : 'transparent',
                                                color: isSubItemActive ? nav.activeText : nav.textMuted,
                                                cursor: 'pointer',
                                                borderRadius: 1.5,
                                                mb: 0.25,
                                                border: isSubItemActive ? `1px solid ${nav.activeBorder}` : '1px solid transparent',
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    backgroundColor: isSubItemActive ? nav.activeBg : nav.hover,
                                                    border: isSubItemActive ? `1px solid ${nav.activeHoverBorder}` : `1px solid ${nav.border}`,
                                                    transform: 'translateX(1px)',
                                                    boxShadow: nav.activeShadow
                                                },
                                                '&:active': {
                                                    transform: 'translateX(0.5px)',
                                                    boxShadow: nav.activeShadow
                                                }
                                            }}
                                        >
                                            <ListItemIcon sx={{
                                                color: isSubItemActive ? nav.activeText : nav.textMuted,
                                                minWidth: 36,
                                                transition: 'color 0.2s ease-in-out'
                                            }}>
                                                {subItem.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={t(subItem.textKey)}
                                                sx={{
                                                    color: isSubItemActive ? nav.activeText : nav.textMuted,
                                                    fontWeight: isSubItemActive ? '600' : '400',
                                                    fontSize: '0.9rem',
                                                    transition: 'all 0.2s ease-in-out'
                                                }}
                                            />
                                        </ListItem>
                                        );
                                    })}
                                </List>
                            )}
                        </React.Fragment>
                        );
                    })}
                </List>

                <Divider sx={{ borderColor: nav.border }} />

                {/* Login/Logout */}
                <List sx={{ px: 1 }}>
                    <ListItem
                        component="button"
                        onClick={handleLogoutFromMenu}
                        sx={{
                            /** Native <button> would otherwise paint the UA light grey. */
                            backgroundColor: 'transparent',
                            color: nav.textMuted,
                            width: '100%',
                            cursor: 'pointer',
                            borderRadius: 2,
                            mb: 0.5,
                            border: '1px solid transparent',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: nav.hover,
                                border: `1px solid ${nav.border}`,
                                transform: 'translateX(2px)',
                                boxShadow: nav.hoverShadow
                            },
                            '&:active': {
                                transform: 'translateX(1px)',
                                boxShadow: nav.activeShadow
                            }
                        }}
                    >
                        <ListItemIcon sx={{
                            color: nav.textMuted,
                            minWidth: 40,
                            transition: 'color 0.2s ease-in-out'
                        }}>
                            {isAuthenticated ? <LogoutIcon /> : <LoginIcon />}
                        </ListItemIcon>
                        <ListItemText
                            primary={isAuthenticated ? t("nav.sign-out") : t("nav.sign-in")}
                            sx={{
                                color: nav.textMuted,
                                fontWeight: '500',
                                fontSize: '0.95rem',
                                transition: 'all 0.2s ease-in-out'
                            }}
                        />
                    </ListItem>
                </List>

                {/* Active Referral Event */}
                <Box sx={{ px: 1, pb: 1 }}>
                    <ReferralEventMiniSection />
                </Box>
            </Drawer>
            ) : null}

            <SubscriptionDialog
                open={subscriptionModalOpen}
                onClose={() => setSubscriptionModalOpen(false)}
                onSubscribe={handleSubscribe}
                triggerReason={subscriptionTriggerReason}
                userSubscription={userSubscription}
            />
        </Box>
        </UserThemeProvider>
    );
};

export default UserLayout;
